import { Component, effect, ElementRef, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { Auth, getIdToken } from '@angular/fire/auth';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { DroneModel } from '../../shared/../drone/shared/drone-model.model';
import { DroneService } from '../../shared/../drone/shared/drone.service';
import { DroneModelsComponentState } from '../../shared/state-interface';
import { StateManager } from '../../shared/state-manager.class';

@Component({
    selector: 'app-drone-models-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule
    ],
    templateUrl: './drone-models.component.html',
    styleUrl: './drone-models.component.css'
})
export class DroneModelsComponent {

    @ViewChild("input") userInput: ElementRef<HTMLInputElement> | undefined;
    @Output() messageEvent = new EventEmitter<string>();

    private auth: Auth = inject(Auth);
    private stateManager: StateManager = inject(StateManager);
    private droneService: DroneService = inject(DroneService);
    
    private static _droneModelsError = "Drone Models error:";

    protected droneModel: FormControl = new FormControl<string | DroneModel>("");
    private _required: boolean = false;
    protected filteredModels: DroneModel[] = [];

    private _droneModels: WritableSignal<DroneModel[]> = signal([]);
    private _model: WritableSignal<string> = signal("");

    public constructor() {
        const effectRef = effect(() => {
            if (this._droneModels().length > 0 && this._model() !== "") {
                for (const droneModel of this._droneModels()) {
                    if (droneModel.type == this._model()) {
                        this.droneModel.setValue(droneModel);
                        break;
                    }
                }
                // Only need/expect to run this effect one time
                // once all conditions are met. Not sure that it
                // is necessary to destroy but doing so to be on
                // the safe side relative to avoiding memory leak.
                effectRef.destroy();
            }
        },
            { manualCleanup: true }
        );
    }

    public getState() {
        const state: DroneModelsComponentState = {} as DroneModelsComponentState;
        state.droneModelControl = this.droneModel;
        state.required = this.required;
        return state;
    }

    public restoreState(state: DroneModelsComponentState) {
        this.droneModel = state.droneModelControl;
        this.required = state.required;
        this._model.set(this.droneModel.value instanceof DroneModel ? this.droneModel.value.type : "");
        this._droneModels.set(this.stateManager.droneModels!);
    }

    public init() {
        if (this._droneModels().length == 0) {
            const droneModels = this.stateManager.droneModels;

            if (droneModels == undefined) {
                this.droneModel.setValue("Loading...");
                this.loadDroneModels();
            } else {
                this._droneModels.set(droneModels);
            }
        }
    }

    public get required(): boolean {
        return this._required;
    }

    public set required(value: boolean) {
        this._required = value;
    }

    public get droneModelCtl(): FormControl {
        return this.droneModel;
    }

    public get selectedModel(): string {
        return this.droneModel.value instanceof DroneModel ? this.droneModel.value.type : "";
    }

    public set model(value: string | undefined) {
        if (value != undefined) {
            this._model.set(value);
            this.droneModel.setValue(value);
        }
    }

    // Note: Must use "arrow" function declaration here
    // in order to have access to 'this'. If using normal
    // function declaration, then 'this' will refer to the
    // mat-select object.
    public displayValueFunc = (droneModel: DroneModel) => {
        if (droneModel instanceof (DroneModel)) {
            return `${droneModel.type}`;
        } else {
            return droneModel;
        }
    }

    public filter(): void {
        if (this.userInput != undefined && this._droneModels().length > 0) {
            const filterValue = this.userInput.nativeElement.value.toLowerCase();
            this.filteredModels = this._droneModels().filter(
                droneModel =>
                    `${droneModel.type} ${droneModel.description}`.toLowerCase().includes(filterValue)
            );
        }
    }

    protected async loadDroneModels() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.droneService.getModels(await getIdToken(currentUser));

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                    try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-drone-models"] == "success") {
                        // this.droneModels is a signal. The view will be refreshed
                        // upon update to the signal.
                        this._droneModels.set(DroneModel.toModels(json["drone-models"]));
                        this.stateManager.droneModels = this._droneModels();
                        this.droneModel.setValue("");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${DroneModelsComponent._droneModelsError} ${(<Error>error).message}`;
                    console.error(message);
                    this.messageEvent.emit(message);
                }
                //}, 2500);
            })
        }
    }
}
